import {css} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';

import {
  automationFileConfigStyles,
  SectionNumber,
  AutomationOutputFileConfigEditorProps,
} from '../AutomationFileConfigUtils';
import {OutputFileAction} from '../RunSchemaSpecConstants';

export enum AutomationOutputFileConfigType {
  ANALYSIS_TEMPLATE = 'analysis_template',
  OUTPUT_FILE = 'output_file',
}

interface AutomationOutputFileConfigTypeSelectorProps extends AutomationOutputFileConfigEditorProps {
  selectedType: AutomationOutputFileConfigType;
  onTypeChange: (type: AutomationOutputFileConfigType) => void;
}

export function AutomationOutputFileConfigTypeSelector(
  props: AutomationOutputFileConfigTypeSelectorProps
): JSX.Element {
  // Store the previous actionType when switching to analysis template
  const [previousActionType, setPreviousActionType] = React.useState<OutputFileAction | null>(null);

  const handleTypeChange = (newType: AutomationOutputFileConfigType): void => {
    if (newType === props.selectedType) {
      return;
    }

    if (newType === AutomationOutputFileConfigType.ANALYSIS_TEMPLATE) {
      if (props.outputFileConfig.actionType !== OutputFileAction.RUN_ANALYSIS_TEMPLATE) {
        setPreviousActionType(props.outputFileConfig.actionType);
      }

      // Reset config for analysis template mode
      props.onUpdateConfig({
        ...props.outputFileConfig,
        analysisTemplate: null,
        actionType: OutputFileAction.RUN_ANALYSIS_TEMPLATE,
      });
    } else {
      const actionTypeToRestore = previousActionType || OutputFileAction.PURE_REGISTRATION;

      props.onUpdateConfig({
        ...props.outputFileConfig,
        analysisTemplate: null,
        actionType: actionTypeToRestore,
      });
    }
    props.onTypeChange(newType);
  };

  return (
    <div
      className={css(automationFileConfigStyles.subsection, automationFileConfigStyles.subsectionBorder)}
      {...getTestProps({component: 'AutomationOutputFileConfigTypeSelector', element: 'root'})}
    >
      <SectionNumber sectionNumber={props.sectionNumber} />
      <div className={css(automationFileConfigStyles.innerTitle)}>Choose configuration type</div>
      <div
        className={css(automationFileConfigStyles.description, automationFileConfigStyles.sectionDescription)}
      >
        Select whether to use an analysis template or configure output file processing directly.
      </div>
      <RadioButtonSelector<AutomationOutputFileConfigType>
        selectedValue={props.selectedType}
        options={[
          {
            label: 'Use output file',
            value: AutomationOutputFileConfigType.OUTPUT_FILE,
            dataTestAttrs: {
              component: 'AutomationOutputFileConfigTypeSelector',
              element: 'outputFile',
            },
          },
          {
            label: 'Use automation template',
            value: AutomationOutputFileConfigType.ANALYSIS_TEMPLATE,
            dataTestAttrs: {
              component: 'AutomationOutputFileConfigTypeSelector',
              element: 'analysisTemplate',
            },
          },
        ]}
        onChange={handleTypeChange}
        isVertical={true}
      />
    </div>
  );
}
